import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { EmailAddressStatus, UpdateEmailStatusByIdRequest } from 'src/app/hickory-api';
import { L10N_LOCALE, L10nLocale, L10nTranslatePipe, L10nTranslationService } from 'angular-l10n';
import { AuthService } from 'src/app/shared/authentication/auth.service';
import { finalize, Observable } from 'rxjs';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { LoadingComponent } from 'src/app/shared/loading/loading.component';

@Component({
  selector: 'app-emails-detail-modal',
  imports: [
    CommonModule,
    L10nTranslatePipe,
    MatDialogModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    LoadingComponent
  ],
  templateUrl: './emails-detail-modal.component.html',
  styleUrl: './emails-detail-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailsDetailModalComponent implements OnInit {
  public readonly locale: L10nLocale = inject(L10N_LOCALE);
  public readonly data: EmailsModalData = inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<EmailsDetailModalComponent>);
  private readonly translation = inject(L10nTranslationService);
  private readonly authService = inject(AuthService);

  readonly hasLoaded = signal<boolean>(true);
  readonly currentEmail = new FormControl('', { validators: [Validators.required, Validators.email] });
  readonly currentStatus = signal<EmailAddressStatus>(undefined);
  readonly currentReason = signal<EmailStatusReason>(undefined);
  readonly isEditing = computed(() => this.data.action === 'Create' || this.currentStatus() !== this.data.status);
  readonly showReason = computed(() => this.data.showReason && this.currentStatus() === EmailAddressStatus.Unsubscribed);

  reasons = [...Object.values(UpdateEmailStatusByIdRequest.ReasonEnum)];

  get statusNameMap() {
    return {
      Active: this.translation.translate(
        'Contacts.Management.Emails.Status.Active',
        {},
        this.locale.language,
      ),
      Unsubscribed: this.translation.translate(
        'Contacts.Management.Emails.Status.Unsubscribed',
        {},
        this.locale.language,
      ),
      Pending: this.translation.translate(
        'Contacts.Management.Emails.Status.Pending',
        {},
        this.locale.language,
      ),
    };
  }

  get isSuperAdmin() {
    return this.authService.isSuperAdmin();
  }

  get canEditEmailStatus() {
    return this.data.action === 'Edit';
  }

  ngOnInit(): void {
    this.currentEmail.setValue(this.data.email ?? '');
    this.currentStatus.set(this.data.status ?? undefined);
    this.currentReason.set(this.data.reason ?? UpdateEmailStatusByIdRequest.ReasonEnum.AdminInitiated);

    if (this.canEditEmailStatus) {
      this.currentEmail.disable();
    }
  }

  onSaveClick(): void {
    if (this.data.action == 'Create') {
      this.hasLoaded.set(false);
      this.data
        .save({ email: this.currentEmail.value ?? '' })
        .pipe(finalize(() => this.hasLoaded.set(true)))
        .subscribe({
          next: (result) => {
            this.dialogRef.close(result);
          },
          error: () => {}
        });
      return;
    }

    if (this.data.action == 'Edit' && this.currentStatus() !== this.data.status) {
      this.hasLoaded.set(false);
      this.data
        .save({ status: this.currentStatus(), reason: this.showReason() ? this.currentReason() : undefined })
        .pipe(finalize(() => this.hasLoaded.set(true)))
        .subscribe({
          next: () => {
            this.dialogRef.close(true);
          },
          error: () => {}
        });
    }
  }
}

export interface EmailsModalData {
  email: string;
  status: EmailAddressStatus;
  availableStatuses: EmailAddressStatus[];
  reason?: EmailStatusReason;
  showReason: boolean;
  save: (result: {
    email?: string;
    status?: EmailAddressStatus;
    reason?: EmailStatusReason;
  }) => Observable<unknown>;
  action: 'Create' | 'Edit' | 'View';
}

export type EmailStatusReason = UpdateEmailStatusByIdRequest.ReasonEnum;
